/**
 * Formattazione di valori numerici e date per la UI.
 *
 * Gli importi arrivano dall'API come STRINGHE decimali (ADR-0006). Qui vengono
 * convertiti in `number` SOLO per la formattazione: nessun calcolo monetario
 * deve avvenire lato client. Se in futuro servissero aggregazioni nel browser,
 * si introduce una libreria decimale — non `parseFloat`.
 */

const LOCALE = 'it-IT';

/** Converte una stringa decimale in number ai soli fini di visualizzazione. */
function toDisplayNumber(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function formatCurrency(
  value: string | number | null | undefined,
  currency = 'EUR',
  options: { maximumFractionDigits?: number } = {},
): string {
  const numeric = toDisplayNumber(value);
  if (numeric === null) return '—';

  return new Intl.NumberFormat(LOCALE, {
    style: 'currency',
    currency: currency.trim() || 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: options.maximumFractionDigits ?? 2,
  }).format(numeric);
}

/** Quantità: fino a 8 decimali per le crypto, senza zeri finali inutili. */
export function formatQuantity(value: string | number | null | undefined): string {
  const numeric = toDisplayNumber(value);
  if (numeric === null) return '—';

  return new Intl.NumberFormat(LOCALE, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 8,
  }).format(numeric);
}

/** `value` è già una percentuale (12.34 → "12,34 %"), non una frazione. */
/**
 * Importo abbreviato — «4,57 Bln $» invece di «4.565.570.158.592,00 $».
 *
 * Serve alle capitalizzazioni di mercato, e solo a quelle: sono l'unico
 * importo del progetto il cui ordine di grandezza è l'informazione, mentre le
 * cifre finali non lo sono. Per qualunque somma che l'utente possa dover
 * riconciliare con un estratto conto resta `formatCurrency`, che le mostra
 * tutte — un'abbreviazione lì nasconderebbe proprio ciò che va confrontato.
 */
export function formatCompactCurrency(
  value: string | number | null | undefined,
  currency = 'EUR',
): string {
  const numeric = toDisplayNumber(value);
  if (numeric === null) return '—';

  return new Intl.NumberFormat(LOCALE, {
    style: 'currency',
    currency: currency.trim() || 'EUR',
    notation: 'compact',
    maximumFractionDigits: 2,
  }).format(numeric);
}

/**
 * ⚠️ Il segno `+` e' acceso per difetto perche' la maggior parte delle
 * percentuali di questa applicazione sono **variazioni**, dove «+2%» e «−2%»
 * sono due fatti opposti e il segno e' l'informazione principale.
 *
 * Non lo sono tutte, pero': un **peso** di portafoglio e' una quota, non una
 * variazione, e scritto «+21,2 %» si legge come una plusvalenza — segnalato
 * dall'utente guardando la tabella delle posizioni. Da qui `signed: false`.
 */
export function formatPercent(
  value: string | number | null | undefined,
  digits = 2,
  options: { signed?: boolean } = {},
): string {
  const numeric = toDisplayNumber(value);
  if (numeric === null) return '—';

  return `${new Intl.NumberFormat(LOCALE, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    signDisplay: options.signed === false ? 'auto' : 'exceptZero',
  }).format(numeric)} %`;
}

export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat(LOCALE).format(value);
}

/** `YYYY-MM-DD` o ISO completo → `17 ago 2026`. */
export function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value.length === 10 ? `${value}T00:00:00Z` : value);
  if (Number.isNaN(date.getTime())) return '—';

  return new Intl.DateTimeFormat(LOCALE, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: value.length === 10 ? 'UTC' : undefined,
  }).format(date);
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  return new Intl.DateTimeFormat(LOCALE, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

/** Segno di un importo, per decidere il colore senza rifare il parsing altrove. */
export function signOf(value: string | number | null | undefined): 'positive' | 'negative' | 'zero' {
  const numeric = toDisplayNumber(value);
  if (numeric === null || numeric === 0) return 'zero';
  return numeric > 0 ? 'positive' : 'negative';
}

/** Etichette leggibili per gli enum del backend. */
export const PORTFOLIO_TYPE_LABELS: Record<string, string> = {
  securities: 'Titoli',
  crypto: 'Criptovalute',
  fixed_income: 'Reddito fisso',
  private_markets: 'Investimenti privati',
  cash: 'Liquidità',
  mixed: 'Misto',
  // Creazione manuale (TODO.md).
  bank_custody: 'Deposito titoli banca',
  paper: 'Portafoglio simulato',
};

export const TX_TYPE_LABELS: Record<string, string> = {
  buy: 'Acquisto',
  sell: 'Vendita',
  dividend: 'Dividendo',
  interest: 'Interessi',
  deposit: 'Versamento',
  withdrawal: 'Prelievo',
  fee: 'Commissione',
  tax: 'Imposta',
  savings_plan: 'Piano di accumulo',
  staking_reward: 'Ricompensa staking',
  transfer_in: 'Trasferimento in entrata',
  transfer_out: 'Trasferimento in uscita',
  split: 'Split azionario',
  other: 'Altro',
};

export const INSTRUMENT_TYPE_LABELS: Record<string, string> = {
  stock: 'Azione',
  etf: 'ETF',
  crypto: 'Crypto',
  bond: 'Obbligazione',
  fund: 'Fondo',
  private_equity: 'Private equity',
  cash: 'Liquidità',
  other: 'Altro',
};

export const ORDER_TYPE_LABELS: Record<string, string> = {
  market: 'Al mercato',
  limit: 'Limite',
  stop: 'Stop (stop-loss)',
  stop_limit: 'Stop-limite',
  trailing_stop: 'Trailing stop',
};

export const TIME_IN_FORCE_LABELS: Record<string, string> = {
  day: 'Valido per la giornata',
  gtc: 'Valido fino a cancellazione (GTC)',
  ioc: 'Immediato o annulla (IOC)',
  fok: 'Tutto o niente (FOK)',
};

export function labelFor(map: Record<string, string>, key: string): string {
  return map[key] ?? key;
}
